import { useEffect } from 'react';
import { getCieloTokenEndpoint } from '../config';

export const getCielo3DSToken = async (): Promise<string> => {
    const url = getCieloTokenEndpoint();

    const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
        throw new Error(`Error fetching Cielo token: ${response.status}`);
    }

    const raw = await response.text();

    const extractToken = (payload: unknown): string | null => {
        if (!payload) return null;
        if (typeof payload === 'object' && !Array.isArray(payload)) {
            const obj = payload as Record<string, any>;
            return obj.access_token || obj.accessToken || obj.token || null;
        }
        if (Array.isArray(payload)) {
            const joined = (payload as unknown[]).map(String).join('');
            try {
                const parsed = JSON.parse(joined);
                if (parsed && typeof parsed === 'object') {
                    return parsed.access_token || parsed.accessToken || parsed.token || null;
                }
            } catch {
                const m = joined.match(/"access_token"\s*:\s*"([^"]+)"/);
                if (m) return m[1];
            }
            return null;
        }
        if (typeof payload === 'string') {
            try {
                const parsed = JSON.parse(payload);
                return extractToken(parsed);
            } catch {
                const m = payload.match(/"access_token"\s*:\s*"([^"]+)"/);
                if (m) return m[1];
            }
        }
        return null;
    };

    let token: string | null = null;
    try {
        const asJson = JSON.parse(raw);
        token = extractToken(asJson);
    } catch {
        token = extractToken(raw);
    }

    if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invalid Cielo 3DS access token from backend');
    }

    return token;
};

declare global {
    interface Window {
        bpmpi_config?: () => any;
        bpmpi_authenticate?: () => void;
        Cardinal?: any;
    }
}

type ThreeDSecureProps = {
    accessToken: string;
    amount: number;
    orderNumber: string;
    currency: string;
    paymentMethod: 'credit' | 'debit';
    cardNumber: string;
    cardExpirationMonth: string;
    cardExpirationYear: string;
    onAuthResult?: (result: ThreeDSResult) => void;
};

export type ThreeDSResult = {
    cavv?: string | null;
    xid?: string | null;
    eci?: string | null;
    version?: string | null;
    referenceId?: string | null;
};

export function ThreeDSecure({
    accessToken,
    amount,
    orderNumber,
    currency,
    paymentMethod,
    cardNumber,
    cardExpirationMonth,
    cardExpirationYear,
    onAuthResult
}: ThreeDSecureProps) {
    type Environment = 'SDB' | 'PRD';

    function getEnvironmentFromScriptUrl(url: string): Environment {
        return url.includes('sandbox') ? 'SDB' : 'PRD';
    }

    const cielo3dsScriptUrl = import.meta.env.VITE_CIELO_3DS_SCRIPT;
    const environment: Environment = getEnvironmentFromScriptUrl(cielo3dsScriptUrl || '');

    useEffect(() => {
        if (!cielo3dsScriptUrl) {
            console.error('VITE_CIELO_3DS_SCRIPT não configurado');
            return;
        }

        window.bpmpi_config = () => ({
            Environment: environment,
            Debug: true,
            onSuccess: (e: any) => {
                onAuthResult?.({
                    cavv: e.Cavv,
                    xid: e.Xid,
                    eci: e.Eci,
                    version: e.Version,
                    referenceId: e.ReferenceId
                });
            },
            onFailure: (e: any) => {
                onAuthResult?.({
                    cavv: null,
                    xid: e.Xid,
                    eci: e.Eci,
                    version: e.Version,
                    referenceId: e.ReferenceId
                });
            },
            onUnenrolled: (e: any) => {
                onAuthResult?.({
                    cavv: null,
                    xid: e.Xid,
                    eci: e.Eci,
                    version: e.Version,
                    referenceId: e.ReferenceId
                });
            },
            onDisabled: () => {
                onAuthResult?.({});
            },
            onError: () => {
                onAuthResult?.({});
            },
            onUnsupportedBrand: () => {
                onAuthResult?.({});
            }
        });

        const loadScript = () => {
            const existing = document.querySelector(`script[src="${cielo3dsScriptUrl}"]`);
            
            const runAuth = () => {
                if (typeof window.bpmpi_authenticate === 'function') {
                    window.bpmpi_authenticate();
                } else {
                    console.error('bpmpi_authenticate não disponível');
                }
            };

            const loadCardinalScript = () => {
                const cardinalUrl = environment === 'SDB' 
                    ? 'https://songbirdstag.cardinalcommerce.com/edge/v1/songbird.js'
                    : 'https://songbird.cardinalcommerce.com/edge/v1/songbird.js';
                
                const existingCardinal = document.querySelector(`script[src="${cardinalUrl}"]`);
                
                if (existingCardinal) {
                    loadCieloScript();
                } else {
                    const cardinalScript = document.createElement('script');
                    cardinalScript.src = cardinalUrl;
                    cardinalScript.async = true;
                    cardinalScript.onload = () => loadCieloScript();
                    cardinalScript.onerror = () => {
                        console.error('Falha ao carregar Cardinal Commerce SDK');
                        loadCieloScript();
                    };
                    document.head.appendChild(cardinalScript);
                }
            };

            const loadCieloScript = () => {
                if (existing) {
                    setTimeout(runAuth, 100);
                } else {
                    const script = document.createElement('script');
                    script.src = cielo3dsScriptUrl;
                    script.async = true;
                    script.onload = () => setTimeout(runAuth, 100);
                    script.onerror = () => console.error('Falha ao carregar SDK Cielo');
                    document.head.appendChild(script);
                }
            };

            loadCardinalScript();
        };

        loadScript();

        const observer = new MutationObserver(() => {
            const iframe = document.querySelector("iframe[name='bpmpi_frame']");
            if (iframe) {
                iframe.setAttribute('style', `
          width: 100%;
          max-width: 500px;
          height: auto;
          border: none;
          overflow: hidden;
        `);
                iframe.parentElement?.setAttribute('style', `
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        `);
                observer.disconnect();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, [
        accessToken,
        environment,
        orderNumber,
        currency,
        amount,
        paymentMethod,
        cardNumber,
        cardExpirationMonth,
        cardExpirationYear,
        onAuthResult
    ]);

    return (
        <div style={{ display: 'none' }}>
            <input type="hidden" className="bpmpi_auth" value="true" />
            <input type="hidden" className="bpmpi_accesstoken" value={accessToken} />
            <input type="hidden" className="bpmpi_ordernumber" value={orderNumber} />
            <input type="hidden" className="bpmpi_currency" value={currency} />
            <input type="hidden" className="bpmpi_totalamount" value={String(amount)} />
            <input type="hidden" className="bpmpi_paymentmethod" value={paymentMethod} />
            <input type="hidden" className="bpmpi_cardnumber" value={cardNumber} />
            <input type="hidden" className="bpmpi_cardexpirationmonth" value={cardExpirationMonth} />
            <input type="hidden" className="bpmpi_cardexpirationyear" value={cardExpirationYear} />
        </div>
    );
}
